/**
 * reportDnd — transform-aware grid math for the dnd-kit report canvas (Epic 37
 * canvas migration off react-grid-layout).
 *
 * THE bug this module exists to get right: the canvas lives inside a CSS
 * `translate(panX,panY) scale(scale)` transform (the ZoomWrapper). RGL's external-
 * drop path divided its cursor-centering offset by `transformScale` along with the
 * screen delta, which over-scaled the offset and made the drop placeholder drift
 * away from the cursor at any zoom ≠ 100% (RGL issues #350 / #2148, PR #1531). We
 * avoid that by doing ALL math in ONE space:
 *
 *   - `getBoundingClientRect()` already returns the on-screen (scaled) rect, so
 *     `clientX - rect.left` is a correct SCREEN-space delta.
 *   - Divide that delta by `scale` ONCE to get the GRID-space coordinate.
 *   - The cursor-centering offset is expressed in GRID space and subtracted
 *     AFTER the divide — never scaled. (This is the exact step RGL got wrong.)
 *
 * Pure functions only — no React, no DOM mutation — so they're unit-testable in
 * jsdom without a live transform.
 */

import { GRID_COLUMNS, GRID_GAP, ROW_HEIGHT_UNIT } from "./gridUtils";

/** The drag payload identity carried on a dnd-kit draggable from the palette. */
export interface PaletteDragData {
  source: "palette";
  type: string;
  colSpan: number;
  rowSpan: number;
  integrationId?: string;
  platform?: string;
  /** Human label, for the DragOverlay + a11y. */
  label: string;
  /**
   * Optional pre-configured widget config carried by the drag (content-block
   * category cards — configure-then-DRAG parity with configure-then-add). When
   * present, the drop seeds it over the type's defaults and the DragOverlay
   * ghost renders it, so what you dragged is exactly what lands (never blank).
   */
  config?: Record<string, unknown>;
}

/** A target grid cell computed from a pointer position. */
export interface GridCell {
  row: number;
  col: number;
}

/**
 * Per-format grid pitch in GRID-space (unscaled) px. `cellWidth` is derived from
 * the grid element's UNSCALED content width so it matches how widgets are laid
 * out; callers pass `contentWidthPx` from getGridDimensions for the active format.
 */
export interface GridMetrics {
  /** Unscaled content width of the grid (px), e.g. getGridDimensions().contentWidthPx. */
  contentWidthPx: number;
  /** Unscaled row height unit (px) for the active format. */
  rowHeightPx: number;
  /** Gap between cells (px). */
  gapPx: number;
  /** Inner padding of the grid container (px), matched to RGL's containerPadding. */
  paddingPx: number;
}

/** Default metrics for the document format (A4) at the canvas's 12-col grid. */
export const DEFAULT_GRID_METRICS: GridMetrics = {
  contentWidthPx: 0, // caller MUST override per format; 0 guards against silent misuse
  rowHeightPx: ROW_HEIGHT_UNIT,
  gapPx: GRID_GAP,
  paddingPx: 24,
};

/** The unscaled width of one of the 12 columns, given the content width + gaps. */
export function columnWidthPx(metrics: GridMetrics): number {
  const inner = metrics.contentWidthPx - 2 * metrics.paddingPx;
  return (inner - (GRID_COLUMNS - 1) * metrics.gapPx) / GRID_COLUMNS;
}

/**
 * Convert a SCREEN-space pointer to a snapped grid cell, transform-safe.
 *
 * @param pointer      Screen coords (clientX/clientY) of the drop point — typically
 *                     the CENTER of the dragged item so the cell sits under the item.
 * @param gridRect     The grid element's getBoundingClientRect() (scaled, on-screen).
 * @param scale        The active zoom scale (zoomLevel / 100).
 * @param metrics      Unscaled grid pitch for the active format.
 * @param span         The dragged widget's { colSpan, rowSpan }, to clamp the column
 *                     so the widget never overflows the 12-col grid.
 */
export function pointerToGridCell(
  pointer: { x: number; y: number },
  gridRect: { left: number; top: number },
  scale: number,
  metrics: GridMetrics,
  span: { colSpan: number; rowSpan: number },
): GridCell {
  const safeScale = scale > 0 ? scale : 1;
  // Screen delta → grid-space (divide by scale ONCE). Subtract padding in grid space.
  const gridX = (pointer.x - gridRect.left) / safeScale - metrics.paddingPx;
  const gridY = (pointer.y - gridRect.top) / safeScale - metrics.paddingPx;

  const colPitch = columnWidthPx(metrics) + metrics.gapPx;
  const rowPitch = metrics.rowHeightPx + metrics.gapPx;

  const rawCol = Math.floor(gridX / colPitch);
  const rawRow = Math.floor(gridY / rowPitch);

  // Clamp column so the widget fits; clamp row to ≥ 0 (free vertical placement —
  // NO upper clamp, so a drop into empty rows far down the page stays there).
  const maxCol = GRID_COLUMNS - span.colSpan;
  const col = Math.max(0, Math.min(rawCol, Math.max(0, maxCol)));
  const row = Math.max(0, rawRow);
  return { row, col };
}

/**
 * Pixel rectangle (in GRID space, unscaled) for a placeholder/preview at a given
 * cell + span — used to render the snap placeholder and to size the DragOverlay.
 * Mirrors the server PDF renderer's positioning math (col/row × pitch) so the
 * editor placeholder lands exactly where the widget — and the PDF — will be.
 */
export function cellToPixelRect(
  cell: GridCell,
  span: { colSpan: number; rowSpan: number },
  metrics: GridMetrics,
): { left: number; top: number; width: number; height: number } {
  const colW = columnWidthPx(metrics);
  const rowH = metrics.rowHeightPx;
  const gap = metrics.gapPx;
  return {
    left: metrics.paddingPx + cell.col * (colW + gap),
    top: metrics.paddingPx + cell.row * (rowH + gap),
    width: span.colSpan * colW + (span.colSpan - 1) * gap,
    height: span.rowSpan * rowH + (span.rowSpan - 1) * gap,
  };
}

/**
 * Apply a SCREEN-space drag delta (dnd-kit `transform.x/y`) to a widget's cell,
 * transform-safe. Divides the delta by `scale` (grid space), then snaps to the
 * nearest col/row pitch. Clamps col so the span fits; row is free (≥ 0).
 */
export function applyMoveDelta(
  from: GridCell,
  delta: { x: number; y: number },
  scale: number,
  metrics: GridMetrics,
  span: { colSpan: number; rowSpan: number },
): GridCell {
  const safeScale = scale > 0 ? scale : 1;
  const colPitch = columnWidthPx(metrics) + metrics.gapPx;
  const rowPitch = metrics.rowHeightPx + metrics.gapPx;
  const dCol = Math.round(delta.x / safeScale / colPitch);
  const dRow = Math.round(delta.y / safeScale / rowPitch);
  const maxCol = GRID_COLUMNS - span.colSpan;
  const col = Math.max(0, Math.min(from.col + dCol, Math.max(0, maxCol)));
  const row = Math.max(0, from.row + dRow);
  return { row, col };
}

/**
 * Apply a SCREEN-space resize delta (from the SE handle) to a span. Column snaps
 * to the nearest VALID span (every width 1–12, so resize steps a column at a
 * time); row span is free (≥ 1). Transform-safe (delta / scale). Clamps col so
 * the widget fits from its current column.
 */
export function applyResizeDelta(
  fromSpan: { colSpan: number; rowSpan: number },
  col: number,
  delta: { x: number; y: number },
  scale: number,
  metrics: GridMetrics,
  snapColTo: (raw: number) => number,
): { colSpan: number; rowSpan: number } {
  const safeScale = scale > 0 ? scale : 1;
  const colPitch = columnWidthPx(metrics) + metrics.gapPx;
  const rowPitch = metrics.rowHeightPx + metrics.gapPx;
  const rawColSpan = fromSpan.colSpan + delta.x / safeScale / colPitch;
  const rawRowSpan = fromSpan.rowSpan + delta.y / safeScale / rowPitch;
  // Snap col to a valid span, then clamp so col + span ≤ 12.
  let colSpan = snapColTo(Math.max(1, rawColSpan));
  colSpan = Math.max(1, Math.min(colSpan, GRID_COLUMNS - col));
  const rowSpan = Math.max(1, Math.round(rawRowSpan));
  return { colSpan, rowSpan };
}
